import { useCallback, useState } from 'react';
import { push } from 'redux-first-history';
import { Typography, Link as MuiLink, Stack } from '@mui/material';
import { Formik } from 'formik';
import { startEmailSignup } from '@client/slices/helpers/session.reducer';
import { useDispatch } from '@client/hooks/redux.hooks';
import { Button } from '@client/components/common/Button';
import { baseUrl } from '@sda/utils/url';
import { Form } from '@client/components/forms/Form';
import { TextField } from '@client/components/forms/TextField';
import { Checkbox } from '@client/components/forms/Checkbox';
import { Hidden } from '@client/components/forms/Hidden';
import { Modal } from '../../globals/Modal';
import { openLoginModal } from '../Login';
import { SignupSchema } from './signup.schema';
import type { LoginModalProps } from '../common/LoginModalProps.interface';

export const Signup: React.FC<LoginModalProps> = (props) => {
  const {
    destination, onSuccess, successScope, intent,
    acceptsTos = false,
  } = props;

  const [scope, setScope] = useState('email-password-form');
  const dispatch = useDispatch();

  const initialValues = {
    acceptsTos,
    fullName: '',
    email: '',
    password: '',
    acceptsGDPR: false,
  };

  const handleEmailSignupClick = useCallback((values: typeof initialValues) => {
    dispatch(startEmailSignup({ ...values, intent }));
  }, [dispatch, intent]);

  const handleSignupSuccess = useCallback((options?: { checkout: boolean }) => {
    if (successScope) {
      setScope(successScope);
    }

    if (onSuccess && (!options || (options && options.checkout))) {
      onSuccess();
    } else {
      Modal.hide();
      dispatch(push(destination || '/signup/intent'));
    }
  }, [dispatch, destination, onSuccess, successScope]);

  return (
    <Formik
      validationSchema={SignupSchema}
      onSubmit={handleEmailSignupClick}
      initialValues={initialValues}
    >
      {({ setSubmitting }) => (
        <Form>
          <Typography variant="h6">
            Sign Up
          </Typography>

          <TextField
            name="fullName"
            label="Full name"
            fullWidth
            required
          />

          <TextField
            type="email"
            name="email"
            label="Email"
            fullWidth
            required
          />

          <TextField
            type="password"
            name="password"
            label="Password"
            helperText="At least 8 characters, please and thanks!"
            fullWidth
            required
          />

          {!acceptsTos ? (
            <Checkbox
              name="acceptsTos"
              required
            >
              I accept Side Door's
              {' '}

              <MuiLink
                href={baseUrl('terms-of-service')}
                id="sda-terms-of-service-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </MuiLink>
            </Checkbox>
          ) : <Hidden name="acceptsTos" />}

          <Checkbox
            name="acceptsGDPR"
            margin={acceptsTos ? 'normal' : 'dense'}
          >
            Keep me up to date with Side Door
          </Checkbox>

          <Stack
            component="section"
            spacing={2}
          >
            <Button
              id="sda-login-email-password-button"
              type="submit"
              scope={scope}
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onSuccess={handleSignupSuccess}
              onError={() => setSubmitting(false)}
            >
              Create Account
            </Button>

            <Typography
              variant="subtitle1"
              color="textSecondary"
            >
              Already have an account?
              {' '}

              <MuiLink
                id="sda-signup-login-link"
                onClick={() => openLoginModal(props)}
                className="main-login-button"
              >
                Log In
              </MuiLink>
            </Typography>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default Signup;
